import { isEmpty, reject, some, sortBy } from "lodash/fp";

import type {
  NoteItem,
  NoteItemOrChapter,
  VideoReel,
} from "@app/analytics/MeetingReport/types";
import type {
  SessionChapter,
  SessionNote,
  TranscriptTurnParagraph,
} from "@app/common/types";
import { isWithinRange } from "@app/common/utils";

export const isChapter = (obj: NoteItemOrChapter): obj is SessionChapter =>
  "topics" in obj;
export const isNote = (obj: NoteItemOrChapter): obj is SessionNote =>
  !isChapter(obj) && !("type" in obj);

export const getDurationFromClips = (
  clips: VideoReel["clips"],
  currentTime = 0
) => {
  if (isEmpty(clips)) return 0;

  const sortedClips = sortBy("startTimestamp", clips);
  const timeframes: [number, number][] = [];

  sortedClips.forEach((clip) => {
    const previousTimeframe = timeframes.pop();
    const currentTimeframe: [number, number] = [
      Math.max(clip.startTimestamp, currentTime),
      Math.max(clip.endTimestamp, currentTime),
    ];

    if (previousTimeframe) {
      const currentClipIntersectsWithPreviousClip = isWithinRange(
        currentTimeframe[0],
        previousTimeframe,
        { inclusiveEnd: true }
      );

      if (currentClipIntersectsWithPreviousClip) {
        timeframes.push([
          Math.min(currentTimeframe[0], previousTimeframe[0]),
          Math.max(currentTimeframe[1], previousTimeframe[1]),
        ]);
      } else {
        timeframes.push(previousTimeframe);
        timeframes.push(currentTimeframe);
      }
    } else {
      timeframes.push(currentTimeframe);
    }
  });

  return timeframes.reduce(
    (total, timeframe) => total + timeframe[1] - timeframe[0],
    0
  );
};

export const generateTranscriptItems = (
  chapters: SessionChapter[],
  turns: TranscriptTurnParagraph[]
) => {
  return sortBy((item) => item.startTime.toMillis(), [...chapters, ...turns]);
};

export const anyAreEdited = <T extends NoteItemOrChapter>(
  items: T[]
): boolean => {
  return some({ isEdited: true }, items);
};

export const anyAreDeleted = <T extends { isDeleted: boolean }>(
  items: T[] = []
): boolean => {
  return some({ isDeleted: true }, items);
};

/**
 * @param items list of notes (summary item or session note)
 * @param isEdited pre-calculated determination of whether these entities have
 *                 been edited. if not passed, will determine the edited status
 *                 solely within this method
 * */
export const getItemsAndEditedStatus = <T extends NoteItem>(
  items: T[],
  isEdited = false
): { items: T[]; isEdited: boolean } => {
  const nonDeletedItems = reject({ isDeleted: true }, items) as T[];
  const areItemsEdited =
    isEdited ||
    nonDeletedItems.length < items.length ||
    anyAreEdited(nonDeletedItems);

  return {
    items: sortBy("relativeStartTime", nonDeletedItems),
    isEdited: areItemsEdited,
  };
};
